<template>
    <v-col class="py-0" :class="$vuetify.breakpoint.smAndDown ? '' : 'px-0'" style="position:relative;">
        <TableActions
            :mobile-show-filterbar="mobileShowFilterbar"
            :is-selected-account-paid="isSelectedAccountPaid"
            :rich-data-table-controller="richDataTableController"
            :selected-account="selectedAccount"
            @updateMobileShowFilterbar="$emit('updateMobileShowFilterbar', $event)"
        />
        <div style="position: relative">
            <BulkActionBar
                :show-bulk-select="false"
                :items="richDataTableController.getRows()"
                :selected-list-ids="richDataTableController.getSelectedRows().map(x => x.id)"
                :is-mobile="$vuetify.breakpoint.smAndDown"
                :selected-account="selectedAccount"
                :is-selected-account-paid="isSelectedAccountPaid"
                :has-integrations="has_integrations"
                :monitoring-enabled="CompanySettingsHelper.isFeatureMonitoringEnabled(selectedAccount)"
                :can-contract-bulk-actions="canContractBulkActions"
                :relative-elem="bulkActionRelativeElem"
                @monitoring_mode="navigateToMonitoring"
                @failed="(ids) => richDataTableController.setSelectedRows(richDataTableController.getSelectedRows().filter(row => ids && ids.includes(row.id)))"
            />
            <RichDataTable
                ref="bulkActionRelativeElem"
                test-id="clients-list-table"
                :rich-data-table-controller="richDataTableController"
                selectable
            >
                <template v-slot:item.name="{ item }">
                    <div class="narrow-column d-flex align-center">
                        <div class="mr-2">
                            <router-link
                                :to="{ name: 'client_profile', params: { id: item.id }}"
                                style="text-decoration: none"
                            >
                                <ClientAvatar :initials="item.initials" :color="item.color"/>
                            </router-link>
                        </div>

                        <v-tooltip
                            :disabled="item.name.length <= 42"
                            bottom
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <router-link
                                    :to="getClientLink(item.id)"
                                    class="column-text-align"
                                    v-bind="attrs"
                                    :test-id="`${item.name.toLowerCase()}`"
                                    v-on="on"
                                >
                                    {{ item.name }}
                                </router-link>
                            </template>
                            <span>{{ item.name }}</span>
                        </v-tooltip>
                        <v-chip
                            v-if="item['is_account'] === 1"
                            class="mx-1 grey lighten-3"
                            small
                        >
                            <span class="font-weight-medium grey--text text--darken-1">
                                {{ $t("Your company") }}
                            </span>
                        </v-chip>
                    </div>
                </template>
                <template v-slot:item.groups_string="{ item }">
                    <v-tooltip
                        :disabled="item.group_names.join(', ').length <= 42"
                        bottom
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <span
                                v-bind="attrs"
                                v-on="on"
                            >
                                {{ item.group_names.join(', ') }}
                            </span>
                        </template>
                        <span class="tooltiptext">{{ item.group_names.join(', ') }}</span>
                    </v-tooltip>
                </template>
                <template v-slot:item.monitoring="{ item }">
                    {{ item.has_monitor ? $t("Yes") : "" }}
                </template>
                <template v-slot:item.contract_count="{ item }">
                    {{ item.active_contracts.length }}
                </template>
                <template v-slot:item.active_contracts="{ item }">
                    {{ formatInvoicingPeriod(item.active_contracts) }}
                </template>
                <template v-slot:item.created_at="{ item }">
                    {{ item.created_at | format_date }}
                </template>
                <template v-slot:item.updated_at="{ item }">
                    {{ item.updated_at | format_date }}
                </template>
                <template v-slot:item.accounting_software="{ item }">
                    {{ get(item, "_data.accounting_software") }}
                </template>
                <template v-slot:item.cp_is_active="{ item }">
                    {{ get(item, "fields.cp_is_active") ? $t("Yes") : "" }}
                </template>
                <template v-slot:item.subclients="{item}">
                    <RelatedClientsField
                        :related-clients="get(item, 'subclients')"
                    />
                </template>
                <template v-slot:item.parent_id="{item}">
                    <RelatedClientsField
                        :related-clients="getParentClient(item)"
                    />
                </template>
                <template v-slot:item.integration="{ item }">
                    <span class="d-flex align-center justify-end" style="white-space: nowrap">
                        <template v-for="(feature_name, integrationIndex) in getClientIntegrationNames(item)">
                            <v-chip
                                :key="integrationIndex"
                                :class="['grey lighten-3', {'cursor-default': !isIntegrationEditable(feature_name)}]"
                                small
                                @click.stop.prevent="onEditIntegration(item.id, feature_name)"
                            >
                                <span class="black--text text--darken-1">
                                    {{ getManifestData(feature_name, "title") }}
                                </span>
                            </v-chip>
                        </template>

                        <v-btn
                            v-if="hasConfigurableIntegrations && !getClientIntegrationNames(item).length"
                            icon
                            color="grey darken-1"
                            class="d-inline-block"
                            @click.stop.prevent="onEditIntegration(item.id, null)"
                        >
                            <v-icon small>mdi-key</v-icon>
                        </v-btn>
                    </span>
                </template>
                <template v-slot:item.cloud_folder="{ item }">
                    <div :title="get(item, 'fields.cloud_folder.name')" style="white-space: nowrap; max-width: 150px; text-overflow: ellipsis; overflow: hidden">{{ get(item, "fields.cloud_folder.name") }}</div>
                </template>
                <template v-slot:item.action="{ item }">
                    <div class="d-flex justify-end">
                        <icon-button @click.stop="edit_client(item.id)">
                            edit
                        </icon-button>
                        <action-menu
                            :items="actionItems"
                            @action="actionItems[$event].action(item)"
                        />
                    </div>
                </template>
                <template v-slot:default="{ item, header }">
                    <div v-if="isTypeUser(header)">
                        {{ getUsersDisplayText(item, header) }}
                    </div>
                    <div v-else-if="header.hasCustomField">
                        {{ formatCustomFieldValue(item[header.value], header.field, item)}}
                    </div>
                    <div v-else>
                        {{ item[header.value] }}
                    </div>
                </template>
            </RichDataTable>
        </div>
    </v-col>
</template>

<script>
import RichDataTable from "../../../components/RichDataTable/RichDataTable";
import BulkActionBar from "./ClientsListBulkActionBar";
import AccountAvatar from "../../../components/AccountAvatar";
import {computed, ref, watch} from "@vue/composition-api";
import AccountModule from "../../../store/modules/account";
import RightsModule from "../../../composables/use-rights";
import {APP_STORE_APP_TYPES, INVOICE_PERIODS} from "@/helpers/constants";
import {Router} from "../../../composables/Router";
import {getManifestData} from "@/views/AppStore/app-store-manifest";
import DialogModule from "../../../store/modules/dialog";
import ClientModule from "../../../store/modules/client";
import FeatureModule from "@/store/modules/feature";
import AppModule from "../../../store/modules/app";
import CompanySettingsHelper from '../../../helpers/company/settings'
import BookmarkModule from "../../../store/modules/bookmark";
import {$t} from "../../../i18n";
import ColumnController from "../../../components/ColumnController";
import TableActions from "./ClientListTableActions";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import {getRegionAppTitle} from "@/helpers/helpers";
import App from "@/App";
import appStoreDialog from "@/store/modules/AppStoreDialog/appStoreDialog";
import ClientAvatar from "@/components/ClientAvatar";
import ClientAvatarListItem from '../../../components/ClientAvatarListItem.vue'
import UserModule from "../../../store/modules/user";
import {formatCustomFieldValue} from "../../../composables/use-custom-fields";
import TemplateChooseDialog from "../../../components/Emailing/EmailEdit/TemplateChooseDialog.vue";
import RelatedClientsField from "./RelatedClientsField.vue";

export default {
    name: "TableSection",
    methods: {formatCustomFieldValue},
    components: {
        RelatedClientsField,
        TemplateChooseDialog,
        ClientAvatarListItem,
        ClientAvatar,
        RichDataTable,
        BulkActionBar,
        AccountAvatar,
        ColumnController,
        TableActions
    },
    props: {
        selectedAccount: {
            type: Object,
            required: true
        },
        richDataTableController: {
            type: Object,
            required: true
        },
        filterController: {
            type: Object,
            required: true
        },
        isSelectedAccountPaid: {
            type: Boolean,
            required: true
        },
        mobileShowFilterbar: {
            type: Boolean,
            required: true
        }
    },
    setup(props, ctx) {
        const integrations = ref([])
        const {clients, getClient} = ClientModule();
        const bulkActionRelativeElem = ref(null)
        const {appStoreDialogActions} = appStoreDialog()
        const router = Router()
        const {fetchAllApps} = FeatureModule();
        const {isFeatureEnabled, accounts_info} = AccountModule();
        const {setDialogOptions, openDialog, setDialogAction} = DialogModule();
        const {is_admin_or_owner, can_edit_account, can_edit_client} = RightsModule();
        const bookmarkModule = BookmarkModule()
        const appModule = AppModule();
        const userModule = UserModule()

        const billing_enabled = computed(() => isFeatureEnabled(props.selectedAccount.id, "billing", false))

        const canContractBulkActions = computed(() => is_admin_or_owner(props.selectedAccount) && billing_enabled.value)

        const formatInvoicingPeriod = contracts => {
            let contractPeriods = contracts
                .map(contract => {
                    const period = INVOICE_PERIODS().find(period => period.value === contract.invoice_period)
                    return period ? period.text : null
                })

            contractPeriods = contractPeriods.filter(function (item, pos) {
                return contractPeriods.indexOf(item) === pos;
            });

            return contractPeriods.join(', ')
        };

        const edit_client = function (client_id) {
            router.push({name: "client_edit", params: {id: client_id}})
        };

        const change_client_integration = (feature, client_id, action) => {
            clients.value.forEach(function (elem) {
                if (elem.id === client_id) {
                    if (elem._data === undefined || !elem._data || elem._data.integrations === undefined) {
                        elem._data = {integrations: []};
                    }
                    if (action && action === "add") {
                        elem._data.integrations.push(feature.name);
                        // remove duplicate names
                        elem._data.integrations = [...new Set(elem._data.integrations)];
                    } else {
                        for (let i = elem._data.integrations.length - 1; i >= 0; i--) {
                            if (elem._data.integrations[i] === feature.name) {
                                elem._data.integrations.splice(i, 1);
                            }
                        }
                    }
                }
            });
            setDialogAction(null)
        };

        const isIntegrationEditable = feature_name => {
            return has_integrations.value && ['merit_aktiva', 'smart_accounts', null].includes(feature_name)
        }

        const onEditIntegration = (client_id, feature_name) => {
            if (isIntegrationEditable(feature_name)) {
                setDialogOptions({
                    component: () => import("@/views/Dialog/IntegrationClientSettingsDialog"),
                    payload: {
                        client_id,
                        account_id: props.selectedAccount.id,
                        feature_name: feature_name
                    },
                    action: change_client_integration
                });
                setDialogAction(change_client_integration)
                openDialog();
            }
        };

        const getClientIntegrationNames = item => {
            let feature_names = []
            if (item && item._data && item._data['integrations'] !== undefined && Array.isArray(item._data['integrations'])) {
                for (let i = 0; i < item._data['integrations'].length; i++) {
                    let feature_name = item._data['integrations'][i]
                    if (isFeatureEnabled(props.selectedAccount.id, feature_name, false))
                        feature_names.push(feature_name)
                }
            }
            return feature_names
        }

        const has_integrations = computed(() => {
            if (props.selectedAccount.data.is_demo) return false;
            if (!can_edit_account(props.selectedAccount.id)) return false;
            if (!integrations.value.length) return false;
            // fixme :: todo :: return true only when account has active "integration" features
            return true;
        })

        const hasConfigurableIntegrations = computed(() => {
            if (!has_integrations.value) return false
            return integrations.value.some((integration) => ['merit_aktiva', 'smart_accounts'].includes(integration.name))
        })

        const loadAccountIntegrations = async (account_id) => {
            let account_info = accounts_info.value.find(x => x.id === account_id);
            try {
                const apps = await fetchAllApps({company_id: account_id})
                integrations.value = apps
                    .filter(app => app.type === "integration" && account_info.features.find(feature => feature === app.name))
                    .map(app => ({
                        id: app.id,
                        name: app.name,
                        title: getRegionAppTitle(app.name, account_info.region_code),
                        feature_id: app.id
                    }))
            } catch (ex) {
                errorDialog.showError(ex)
            }
        }

        const navigateToMonitoring = () => {
            router.push({
                name: "clients_list_monitoring",
                query: {
                    account_id: props.selectedAccount.id,
                    client_id_list: props.richDataTableController.getSelectedRows().map(x => x.id).join(',')
                }
            });
        }

        watch(() => props.selectedAccount.id, function (val, prev) {
            if (val !== undefined && (val !== prev)) {
                loadAccountIntegrations(val);
            }
        });

        const getClientLink = (clientId) => {
            appModule.selected_account_id = props.filterController.getValue('account_id')[0]
            const bookmark = bookmarkModule.client_tasks_bookmarks.value.find(c => Number(c.name) === props.selectedAccount.id);
            return bookmarkModule.getClientTasksBookmarkLink(clientId, bookmark);
        }

        const actionItems = computed(() => {
            let result = []
            result.push({
                text: $t('View report'),
                action: (item) => router.push({name: 'report', query: {client_id_list: item.id, view: 'detailed'}})
            })
            if (is_admin_or_owner(props.selectedAccount) && billing_enabled.value) {
                result.push({
                    text: $t('Edit billing'),
                    action: (item) => router.push({
                        name: 'client_contract_view',
                        params: {accountId: item.account_id, id: item.id}
                    })
                },)
            }
            return result
        })

        const isTypeUser = (header) => {
            const fieldType = header.field?.type
            return ["user_single", "user_multi"].includes(fieldType)
        }

        const getUsersDisplayText = (item, header) => {
            const valueLocation = header.value
            let ids = item[valueLocation]

            if (!ids) {
                return ""
            }

            if (!Array.isArray(ids)) {
                ids = [ids] // user_single case
            }

            let names = []
            for (const id of ids) {
                const user = userModule.allMembers.value.find(user => user.id === id)
                names.push(user.name)
            }

            return names.join(', ');
        }

        const getParentClient = (item) => {
            const parent_client = item['parent_client']
            return parent_client ? [parent_client] : []
        }

        return {
            canContractBulkActions,
            bulkActionRelativeElem,
            formatInvoicingPeriod,
            edit_client,
            getManifestData,
            onEditIntegration,
            getClientIntegrationNames,
            has_integrations,
            CompanySettingsHelper,
            navigateToMonitoring,
            getClientLink,
            actionItems,
            can_edit_client,
            hasConfigurableIntegrations,
            isIntegrationEditable,
            isTypeUser,
            getUsersDisplayText,
            getParentClient,
        }
    }
}
</script>

<style scoped>
.cursor-default {
    cursor: default !important;
}
</style>